<template>
  <div class="community-feed">
    <div v-if="loadingInitial" class="text-center py-4">
      <b-spinner variant="primary" />
    </div>

    <template v-else>
      <div v-if="!visiblePosts.length" class="empty text-center py-5">
        {{ emptyLabel }}
      </div>

      <template v-if="variant === 'topics'">
        <topic-card
          v-for="post in visiblePosts"
          :key="post.id"
          :post="post"
          :forum-id="forumId"
        />
      </template>
      <template v-else>
        <post-card
          v-for="post in visiblePosts"
          :key="post.id"
          :post="post"
          :can-moderate="canModerate"
          class="mb-3"
          @updated="onPostUpdated"
          @deleted="onPostDeleted"
        />
      </template>

      <div v-if="hasMore" class="mt-3">
        <button
          class="btn-see-more w-100"
          :disabled="loadingMore"
          @click="loadMore"
        >
          <loading :show="loadingMore" small>Ver mais</loading>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import PostCard from "./PostCard.vue";
import TopicCard from "./TopicCard.vue";

const LIMIT = 10;

export default {
  name: "CommunityFeed",
  components: { PostCard, TopicCard },
  props: {
    communityId: {
      type: String,
      default: ""
    },
    forumId: {
      type: String,
      default: null
    },
    canModerate: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: "cards",
      validator: value => ["cards", "topics"].includes(value)
    },
    search: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      posts: [],
      count: 0,
      offset: 0,
      loadingInitial: true,
      loadingMore: false
    };
  },
  computed: {
    hasMore() {
      return this.posts.length < this.count;
    },
    visiblePosts() {
      const query = this.search.trim().toLowerCase();
      if (!query) return this.posts;
      return this.posts.filter(post => {
        const doc = new DOMParser().parseFromString(
          post.text || "",
          "text/html"
        );
        const text = (doc.body.textContent || "").toLowerCase();
        const author = ((post.author && post.author.name) || "").toLowerCase();
        return text.includes(query) || author.includes(query);
      });
    },
    emptyLabel() {
      if (this.search.trim()) return "Nenhum resultado para a busca.";
      if (this.variant === "topics") return "Ainda não há tópicos neste fórum.";
      return this.forumId
        ? "Ainda não há publicações neste fórum."
        : "Ainda não há publicações nesta comunidade.";
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    fetch() {
      if (this.forumId) {
        return this.$store.dispatch("community/listForumPosts", {
          forumId: this.forumId,
          limit: LIMIT,
          offset: this.offset
        });
      }
      return this.$store.dispatch("community/listPosts", {
        id: this.communityId,
        limit: LIMIT,
        offset: this.offset
      });
    },
    reload() {
      this.offset = 0;
      this.loadingInitial = true;
      this.fetch()
        .then(data => {
          this.count = data.count;
          this.posts = data.results;
          this.$emit("count-change", this.count);
        })
        .finally(() => {
          this.loadingInitial = false;
        });
    },
    loadMore() {
      this.offset += LIMIT;
      this.loadingMore = true;
      this.fetch()
        .then(data => {
          this.count = data.count;
          this.posts = [...this.posts, ...data.results];
          this.$emit("count-change", this.count);
        })
        .finally(() => {
          this.loadingMore = false;
        });
    },
    onPostUpdated(updated) {
      const index = this.posts.findIndex(post => post.id === updated.id);
      if (index !== -1) this.$set(this.posts, index, updated);
      this.$emit("count-change", this.count);
    },
    onPostDeleted(id) {
      this.posts = this.posts.filter(post => post.id !== id);
      this.count = Math.max(0, this.count - 1);
      this.$emit("count-change", this.count);
    }
  }
};
</script>

<style lang="scss" scoped>
.community-feed {
  font-family: "Inter", "Noto Sans", sans-serif;

  .empty {
    opacity: 0.7;
  }

  .btn-see-more {
    height: 40px;
    border-radius: 4px;
    border: 1px solid rgba(128, 128, 128, 0.35);
    background: transparent;
    color: inherit;
    font-size: 14px;
    line-height: 20px;

    &:disabled {
      opacity: 0.6;
    }
  }

  .theme-dark & {
    .btn-see-more {
      border-color: #182126;
      color: #bdc9d1;
      background: #0c1013;

      &:hover {
        border-color: #438a98;
      }
    }
  }
}
</style>
